import React, { useState, useCallback, useEffect, useRef } from 'react';
import { User, Loader, Upload, Brain, CheckCircle, Edit3, Search, Globe2, Users } from 'lucide-react';
import { TagInput, Alert } from '../shared';
import { candidatesApi } from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useSofteamboardStore, normalizeStep } from '../../store/softeamboardStore';
import api from '../../services/api';
import TurnoverCandidateTab from './TurnoverCandidateTab';
import RecruitingCreateModalShell from './RecruitingCreateModalShell';
import FormSectionTabs from './FormSectionTabs';

const TURNOVER_ENABLED = process.env.REACT_APP_TURNOVERIT_ENABLED === 'true';

const AddCandidateModal = ({ open, onClose, needId, onSuccess, turnoverEnabled = TURNOVER_ENABLED }) => {
  const { addCandidateToNeed, refreshCandidatesForNeed, addStepToCache } = useSofteamboardStore();
  
  const [activeTab, setActiveTab] = useState('upload');
  const [manualSection, setManualSection] = useState('principal');
  
  // Simplified manual form data to match API structure
  const [manualFormData, setManualFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: [],
    cvUrl: '',
    expectedTjm: '',
    salesforceId: ''
  });

  const [cvFile, setCvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingCV, setUploadingCV] = useState(false);
  const [analyzingCV, setAnalyzingCV] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const turnoverCandidateIdRef = useRef(null);
  const [turnoverBusy, setTurnoverBusy] = useState(false);
  const [turnoverAnalysisError, setTurnoverAnalysisError] = useState(null);
  const turnoverCompletionHandled = useRef(false);
  // Existing candidate search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [availableCandidates, setAvailableCandidates] = useState([]);
  const candidateSearchGenerationRef = useRef(0);

  useEffect(() => {
    if (activeTab !== 'existing' || !open) return undefined;

    const generation = ++candidateSearchGenerationRef.current;
    const timeout = window.setTimeout(async () => {
      try {
        setSearchingCandidates(true);
        const query = searchTerm.trim();
        const response = await candidatesApi.listCandidates({
          limit: 50,
          offset: 0,
          ...(query && { search: query }),
        });
        if (generation === candidateSearchGenerationRef.current) {
          setAvailableCandidates(response.items || []);
        }
      } catch (error) {
        if (generation === candidateSearchGenerationRef.current) {
          console.error('Failed to load existing candidates:', error);
          setAvailableCandidates([]);
          setErrors({ existing: 'Impossible de charger les candidats. Réessayez.' });
        }
      } finally {
        if (generation === candidateSearchGenerationRef.current) {
          setSearchingCandidates(false);
        }
      }
    }, searchTerm.trim() ? 300 : 0);

    return () => {
      window.clearTimeout(timeout);
      if (generation === candidateSearchGenerationRef.current) {
        candidateSearchGenerationRef.current += 1;
      }
    };
  }, [activeTab, open, searchTerm]);

  const filteredCandidates = availableCandidates;

  const handleCandidateSelect = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const createCandidateIdentifiedStep = async (candidateId) => {
    if (!candidateId) throw new Error('candidateId is required');
    if (!needId) throw new Error('needId is required');

    const stepData = {
      type: 'CANDIDATE_IDENTIFIED',
      comment: 'Candidate created and identified for this need',
      date: new Date().toISOString()
    };

    const createdStep = await candidatesApi.createStep(needId, candidateId, stepData);
    addStepToCache(needId, candidateId, normalizeStep(createdStep));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    // Guard clauses — fail fast
    if (!needId) {
      setErrors({ general: 'Le besoin est introuvable. Fermez puis rouvrez cette fenêtre.' });
      return;
    }
    if (!manualFormData.name?.trim()) {
      setErrors({ name: 'Le nom est obligatoire' });
      return;
    }
    if (manualFormData.name.trim().length < 2) {
      setErrors({ name: 'Le nom doit contenir au moins 2 caractères' });
      return;
    }
    if (!manualFormData.email?.trim()) {
      setErrors({ email: 'L’adresse e-mail est obligatoire' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualFormData.email.trim())) {
      setErrors({ email: 'Saisissez une adresse e-mail valide' });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const candidateData = {
        name: manualFormData.name.trim(),
        email: manualFormData.email.trim(),
        phone: manualFormData.phone?.trim() || null,
        skills: manualFormData.skills || [],
        cvUrl: manualFormData.cvUrl?.trim() || 'manual-entry-no-cv',
        expectedTjm: manualFormData.expectedTjm?.trim()
          ? parseFloat(manualFormData.expectedTjm)
          : null,
        salesforceId: manualFormData.salesforceId?.trim() || null
      };

      const { data: createdCandidate, status } = await candidatesApi.addCandidate(needId, candidateData);

      try {
        await createCandidateIdentifiedStep(createdCandidate.id);
      } catch (stepErr) {
        console.warn('Step creation failed (candidate still created):', stepErr.message);
      }

      await addCandidateToNeed(needId, createdCandidate);

      resetForm();
      onSuccess(createdCandidate, { wasExisting: status === 200 });
      onClose();
    } catch (err) {
      console.error('Error adding candidate:', err);
      if (err.response?.status === 422) {
        setErrors({ general: 'Vérifiez les informations saisies. Certains champs sont invalides.' });
      } else {
        setErrors({ general: err.response?.data?.detail || err.message || 'Impossible d’ajouter le candidat' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExistingSubmit = async (e) => {
    e.preventDefault();

    // Guard clauses — fail fast
    if (!needId) {
      setErrors({ general: 'Le besoin est introuvable. Fermez puis rouvrez cette fenêtre.' });
      return;
    }
    if (!selectedCandidate) {
      setErrors({ existing: 'Sélectionnez un candidat à rattacher.' });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const attachedCandidate = await candidatesApi.attachCandidateToNeed(needId, selectedCandidate.id);
      const candidate = attachedCandidate || selectedCandidate;

      try {
        await createCandidateIdentifiedStep(candidate.id);
      } catch (stepErr) {
        console.warn('Step creation failed (candidate still attached):', stepErr.message);
      }

      await addCandidateToNeed(needId, candidate);

      resetForm();
      onSuccess(candidate, { attachedOnly: true });
      onClose();
    } catch (err) {
      console.error('Error attaching candidate:', err);
      setErrors({ general: err.response?.data?.detail || err.message || 'Impossible de rattacher le candidat.' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: Enhanced WebSocket message handler
  const handleWebSocketMessage = useCallback(async (message) => {
    const messageType = message.type || message.event_type;
    const isCandidateMessage = messageType && (
      messageType.includes('candidate_analysis') || 
      messageType === 'candidate_updated' || 
      messageType === 'candidate_created'
    );
    
    if (!isCandidateMessage) {
      return;
    }
    
    const messageData = message.data || message.payload;
    const entityId = message.entity_id;

    const activeTurnoverCandidateId = turnoverCandidateIdRef.current;
    if (activeTurnoverCandidateId) {
      const matchesTurnoverImport = entityId === activeTurnoverCandidateId && message.need_id === needId;
      if (!matchesTurnoverImport || turnoverCompletionHandled.current) return;

      if (messageType === 'candidate_analysis_error') {
        setAnalyzingCV(false);
        setUploadingCV(false);
        setTurnoverBusy(false);
        setTurnoverAnalysisError('Le profil a été ajouté, mais son analyse a échoué. Vous pouvez relancer l’analyse sans recréer le candidat.');
        turnoverCandidateIdRef.current = null;
        setAnalysisJobId(null);
        return;
      }
      if (messageType !== 'candidate_analysis_complete') return;

      turnoverCompletionHandled.current = true;
      await refreshCandidatesForNeed(needId);
      onSuccess({ id: entityId });
      setAnalyzingCV(false);
      setUploadingCV(false);
      setTurnoverBusy(false);
      setAnalysisJobId(null);
      onClose();
      return;
    }
    
    console.log('📨 Candidate analysis message received:', message);
    
    if (messageType === 'candidate_analysis_complete' || messageType === 'candidate_updated') {
      console.log('✅ Candidate analysis completed for:', entityId);
      
      // ✅ FIX: Remove the problematic setAnalysisJobId callback
      // Check if this is the job we're waiting for
      if ((analysisJobId === entityId || analysisJobId === message.entity_id)
        && (!message.need_id || message.need_id === needId)) {
        console.log('✅ Matching message received - processing completion');
        
        // Fetch the created candidate data to check if email is missing
        // Store global WebSocket handler already calls refreshCandidatesForNeed
        setTimeout(async () => {
          try {
            const candidateResponse = await candidatesApi.getCandidate(entityId);
            
            // ✅ NEW: Call success callback with candidate data and missing email flag
            onSuccess({ 
              id: entityId, 
              candidateData: candidateResponse,
              needsEmailEdit: candidateResponse && (!candidateResponse.email || candidateResponse.email.trim() === '')
            });
          } catch (error) {
            console.error('❌ Failed to fetch created candidate:', error);
            // Fallback without candidate data
            onSuccess({ id: entityId });
          }
        }, 200);
        
        // ✅ FIX: Reset state and close modal immediately
        setAnalyzingCV(false);
        setUploadingCV(false);
        setCvFile(null);
        setErrors({});
        setAnalysisJobId(null);
        
        onClose();
        
        console.log('✅ CV Analysis completed successfully!');
      }
    } else if (messageType === 'candidate_analysis_progress') {
      console.log('📊 Candidate analysis progress:', messageData);
    } else if (messageType === 'candidate_analysis_error') {
      console.log('❌ Candidate analysis failed:', messageData);
      setAnalyzingCV(false);
      setUploadingCV(false);
      setErrors({ cvFile: messageData?.error || 'Analysis failed' });
    }
  }, [onSuccess, onClose, needId, refreshCandidatesForNeed, analysisJobId]);
  
  // ✅ FIX: Only use WebSocket for job-specific messages
  const { updateJobId } = useWebSocket(
    localStorage.getItem('access_token'),
    handleWebSocketMessage,
    analysisJobId // Only connect when we have a job ID
  );

  // Update job subscription when analysisJobId changes
  useEffect(() => {
    if (analysisJobId) {
      updateJobId(analysisJobId);
    }
  }, [analysisJobId, updateJobId]);

  const validateField = (field, value) => {
    const newErrors = { ...errors };
    
    switch (field) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'Name is required';
        } else if (value.trim().length < 2) {
          newErrors.name = 'Name must be at least 2 characters';
        } else {
          delete newErrors.name;
        }
        break;
      
      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      
      case 'phone':
        if (value && !/^[+]?[1-9][\d]{0,15}$/.test(value.replace(/[\s\-()]/g, ''))) {
          newErrors.phone = 'Please enter a valid phone number';
        } else {
          delete newErrors.phone;
        }
        break;
      
      default:
        break;
    }
    
    setErrors(newErrors);
  };

  const handleInputChange = (field, value) => {
    setManualFormData(prev => ({
      ...prev,
      [field]: value
    }));
    validateField(field, value);
  };

  // Array field handlers
  const removeArrayItem = (field, itemToRemove) => {
    setManualFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== itemToRemove)
    }));
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      validateCVFile(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateCVFile(file);
    }
    // Clear the input value to allow re-selecting the same file
    e.target.value = '';
  };

  const validateCVFile = file => {
    if (!file) return;
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + (file.name || '').split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setCvFile(null);
      setErrors({ cvFile: 'Sélectionnez un fichier PDF, DOC ou DOCX.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setCvFile(null);
      setErrors({ cvFile: 'Le fichier ne doit pas dépasser 10 Mo.' });
      return;
    }
    setErrors({});
    setCvFile(file);
  };

  // AI Analysis with CV Upload, explicitly triggered from the footer
  const handleCVFileUpload = async (file) => {
    // Guard clauses — validate everything before touching state
    if (!file) return;
    if (!needId) {
      setErrors({ cvFile: 'Need ID is missing. Please close and reopen.' });
      return;
    }

    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + (file.name || '').split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setErrors({ cvFile: 'Please upload a PDF, DOC, or DOCX file' });
      return;
    }

    // All validations passed — now update state
    try {
      setUploadingCV(true);
      setAnalyzingCV(true);
      setErrors({});
      setCvFile(file);

      // Use the analysisIA API for file analysis
      const formData = new FormData();
      formData.append('files', file);
      formData.append('file_types', 'cv');
      formData.append('api_config_json', JSON.stringify({
        api_key: 'default',
        email: 'user@example.com',
        username: 'user',
        need_id: needId
      }));

      // ✅ FIX: Use the configured api instance instead of direct fetch to ensure token refresh
      // 🔍 ENHANCED MONITORING: Log before API call
      console.log('🔍 MONITORING: About to call AI analysis API', {
        timestamp: new Date().toISOString(),
        fileName: file.name,
        fileSize: file.size,
        needId: needId,
        localStorageTokens: {
          accessToken: localStorage.getItem('access_token') ? 'present' : 'missing',
          refreshToken: localStorage.getItem('refresh_token') ? 'present' : 'missing',
          user: localStorage.getItem('user') ? 'present' : 'missing'
        }
      });
      
      const response = await api.post('/analyses-ia/file-analyses', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const analysisResponse = response.data;
      
      // 🔍 ENHANCED MONITORING: Log after successful API call
      console.log('🔍 MONITORING: AI analysis API call successful', {
        timestamp: new Date().toISOString(),
        responseData: analysisResponse,
        localStorageTokens: {
          accessToken: localStorage.getItem('access_token') ? 'present' : 'missing',
          refreshToken: localStorage.getItem('refresh_token') ? 'present' : 'missing',
          user: localStorage.getItem('user') ? 'present' : 'missing'
        }
      });
      
      if (analysisResponse.results && analysisResponse.results.length > 0) {
        const result = analysisResponse.results[0];
        setAnalysisJobId(result.entity_id);
        
        console.log('🔄 Analysis started, waiting for WebSocket updates...');
      } else {
        throw new Error('No analysis results received');
      }

    } catch (error) {
      console.error('AI analysis failed:', error);
      
      // 🔍 ENHANCED MONITORING: Log AI analysis failure
      console.error('🔍 MONITORING: AI analysis failed', {
        timestamp: new Date().toISOString(),
        error: error.message,
        errorStack: error.stack,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        localStorageTokens: {
          accessToken: localStorage.getItem('access_token') ? 'present' : 'missing',
          refreshToken: localStorage.getItem('refresh_token') ? 'present' : 'missing',
          user: localStorage.getItem('user') ? 'present' : 'missing'
        }
      });
      
      setErrors({ cvFile: `AI analysis failed: ${error.message}` });
      setUploadingCV(false);
      setAnalyzingCV(false);
    }
  };

  const handleClose = () => {
    if (!loading && !uploadingCV && !analyzingCV && !turnoverBusy) {
      resetForm();
      onClose();
    }
  };

  const resetForm = useCallback(() => {
    setManualFormData({
      name: '',
      email: '',
      phone: '',
      skills: [],
      cvUrl: '',
      expectedTjm: '',
      salesforceId: ''
    });
    setCvFile(null);
    setErrors({});
    setActiveTab('upload');
    setManualSection('principal');
    setSearchTerm('');
    setSelectedCandidate(null);
    
    setUploadingCV(false);
    setAnalyzingCV(false);
    setDragActive(false);
    setAnalysisJobId(null);
    turnoverCandidateIdRef.current = null;
    setTurnoverBusy(false);
    setTurnoverAnalysisError(null);
    turnoverCompletionHandled.current = false;
    setLoading(false);
    
    // FIXED: Reset file input elements
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.value = '';
    });
  }, []);

  // FIXED: Reset form immediately when modal opens, no delay
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, needId, resetForm]);

  const handleApiError = (error) => {
    console.error('API Error:', error);
    if (error.response?.data?.detail) {
      setErrors({ general: error.response.data.detail });
    } else {
      setErrors({ general: error.message || 'An unexpected error occurred' });
    }
  };

  const methods = [
    { id: 'upload', label: 'Importer un CV', description: 'Créer le profil à partir d’un fichier', icon: Upload },
    { id: 'manual', label: 'Saisir un candidat', description: 'Renseigner le profil manuellement', icon: Edit3 },
    { id: 'existing', label: 'Choisir dans les candidats', description: 'Rattacher un profil déjà présent', icon: Users },
    ...(turnoverEnabled ? [{ id: 'turnover', label: 'Rechercher sur Turnover-IT', description: 'Importer depuis la CVthèque', icon: Globe2 }] : []),
  ];
  const tabsLocked = uploadingCV || analyzingCV || turnoverBusy;

  const primaryButtonClass = 'min-h-[44px] rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  const secondaryButtonClass = 'min-h-[44px] rounded-lg border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2';

  const footerActions = (
    <>
      <button type="button" onClick={handleClose} disabled={tabsLocked || loading} className={secondaryButtonClass}>Annuler</button>
      {activeTab === 'upload' && (
        <button type="button" onClick={() => handleCVFileUpload(cvFile)} disabled={!cvFile || tabsLocked} className={primaryButtonClass}>
          {tabsLocked ? 'Analyse en cours…' : 'Ajouter et analyser'}
        </button>
      )}
      {activeTab === 'manual' && (
        <button type="submit" form="candidate-manual-form" disabled={loading || !manualFormData.name.trim() || !manualFormData.email.trim()} className={primaryButtonClass}>
          {loading ? 'Ajout en cours…' : 'Ajouter le candidat'}
        </button>
      )}
      {activeTab === 'existing' && (
        <button type="button" onClick={handleExistingSubmit} disabled={loading || !selectedCandidate} className={primaryButtonClass}>
          {loading ? 'Ajout en cours…' : 'Ajouter ce candidat'}
        </button>
      )}
    </>
  );

  return (
    <RecruitingCreateModalShell
      open={open}
      onClose={handleClose}
      title="Ajouter un candidat"
      subtitle="Importez un nouveau profil ou rattachez un candidat déjà connu."
      icon={User}
      methods={methods}
      activeMethod={activeTab}
      onMethodChange={setActiveTab}
      busy={tabsLocked || loading}
      footerNote={activeTab === 'upload' ? (cvFile ? '1 fichier prêt · analyse au clic uniquement' : 'PDF, DOC ou DOCX · 10 Mo maximum') : 'Les modifications sont enregistrées uniquement après validation.'}
      footerActions={footerActions}
    >
      {errors.general && <div className="mb-6"><Alert variant="error">{errors.general}</Alert></div>}
      {activeTab === 'upload' && (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold text-gray-900">Importer un CV</h3><p className="mt-1 text-sm text-gray-600">Le CV sera analysé pour préremplir l’identité, les expériences et les compétences.</p></div>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-400 bg-blue-50' 
                      : uploadingCV || analyzingCV
                      ? 'border-gray-300 bg-gray-50'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {uploadingCV || analyzingCV ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <Brain className="animate-pulse text-purple-600" size={48} />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900">
                          {uploadingCV ? 'Envoi du fichier…' : 'Analyse en cours…'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Cette opération peut prendre quelques minutes. La fenêtre se fermera automatiquement.
                        </p>
                      </div>
                    </div>
                  ) : cvFile ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <CheckCircle className="text-green-600" size={48} />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-green-600">
                          Fichier prêt : {cvFile.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          Aucune analyse ne démarre avant votre confirmation.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <Upload className="text-gray-400" size={48} />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900 mb-2">
                          Déposez le CV ici
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          ou sélectionnez un fichier sur votre ordinateur
                        </p>
                        <label className="inline-flex min-h-[44px] cursor-pointer items-center rounded-lg bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
                          <Upload size={16} className="mr-2" />
                          Choisir un CV
                          <input
                            type="file"
                            onChange={handleFileSelect}
                            className="hidden"
                            accept=".pdf,.doc,.docx"
                            disabled={uploadingCV || analyzingCV}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        Formats acceptés : PDF, DOC, DOCX (10 Mo maximum)
                      </p>
                    </div>
                  )}
                </div>
                
                {errors.cvFile && (
                  <p className="text-sm text-red-600 text-center" role="alert">
                    {errors.cvFile}
                  </p>
                )}
        </div>
      )}
      {activeTab === 'manual' && (
        <form id="candidate-manual-form" onSubmit={handleManualSubmit} className="space-y-6">
          <div><h3 className="text-lg font-semibold text-gray-900">Saisir les informations du candidat</h3><p className="mt-1 text-sm text-gray-600">Créez une fiche exploitable maintenant ; elle pourra être complétée ensuite.</p></div>
          <FormSectionTabs
            tabs={[
              { id: 'principal', label: 'Principal' },
              { id: 'complementaire', label: 'Complémentaire' },
              { id: 'integrations', label: 'Intégrations' },
            ]}
            activeTab={manualSection}
            onChange={setManualSection}
            label="Rubriques du candidat"
            idPrefix="candidate-sections"
          />
          {manualSection === 'principal' && <div id="candidate-sections-principal-panel" role="tabpanel" aria-labelledby="candidate-sections-principal-tab" className="space-y-6">
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <h4 className="mb-4 font-semibold text-gray-900">Identité</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={manualFormData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="e.g., John Doe"
                        disabled={loading}
                        required
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={manualFormData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="e.g., john.doe@example.com"
                        disabled={loading}
                        required
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={manualFormData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="e.g., +33 1 23 45 67 89"
                        disabled={loading}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                      )}
                    </div>
            </div>
          </section>
          <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
            <h4 className="font-semibold text-gray-900">Profil</h4>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Compétences</label>
                  <TagInput
                    items={manualFormData.skills}
                    onAdd={(value) => setManualFormData(prev => ({ ...prev, skills: [...prev.skills, value] }))}
                    onRemove={(value) => removeArrayItem('skills', value)}
                    placeholder="Ajouter une compétence…"
                    color="blue"
                    disabled={loading}
                  />
            </div>
          </section>
          </div>}
          {manualSection === 'complementaire' && <div id="candidate-sections-complementaire-panel" role="tabpanel" aria-labelledby="candidate-sections-complementaire-tab" className="grid gap-4 md:grid-cols-2">
            <div><label htmlFor="expectedTjm" className="mb-2 block text-sm font-medium text-gray-700">TJM attendu</label><input id="expectedTjm" type="number" min="0" value={manualFormData.expectedTjm} onChange={e => handleInputChange('expectedTjm', e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" disabled={loading} /></div>
            <div><label htmlFor="cvUrl" className="mb-2 block text-sm font-medium text-gray-700">URL du CV (optionnel)</label><input id="cvUrl" type="url" value={manualFormData.cvUrl} onChange={e => handleInputChange('cvUrl', e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" disabled={loading} /></div>
          </div>}
          {manualSection === 'integrations' && <div id="candidate-sections-integrations-panel" role="tabpanel" aria-labelledby="candidate-sections-integrations-tab">
            <label htmlFor="salesforceId" className="mb-2 block text-sm font-medium text-gray-700">Identifiant Salesforce (optionnel)</label><input id="salesforceId" value={manualFormData.salesforceId} onChange={e => handleInputChange('salesforceId', e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" disabled={loading} />
          </div>}
        </form>
      )}
      {activeTab === 'existing' && (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold text-gray-900">Choisir un candidat existant</h3><p className="mt-1 text-sm text-gray-600">Recherchez un profil déjà connu pour le rattacher à ce besoin sans le dupliquer.</p></div>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      aria-label="Rechercher un candidat"
                      placeholder="Rechercher par nom, e-mail ou compétence…"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={searchingCandidates}
                    />
                  </div>

                  {/* Candidates List */}
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    {searchingCandidates ? (
                      <div className="p-4 text-center text-gray-500">
                        <Loader className="animate-spin mx-auto mb-2" size={20} />
                        Chargement des candidats…
                      </div>
                    ) : filteredCandidates.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {searchTerm ? 'Aucun candidat ne correspond à votre recherche.' : 'Aucun candidat disponible.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredCandidates.map((candidate) => (
                          <button
                            type="button"
                            key={candidate.id}
                            onClick={() => handleCandidateSelect(candidate)}
                            className={`w-full p-4 text-left cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedCandidate?.id === candidate.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                                <p className="text-sm text-gray-600">{candidate.email}</p>
                                {candidate.skills && candidate.skills.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {candidate.skills.slice(0, 3).map((skill, index) => (
                                      <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                        {skill}
                                      </span>
                                    ))}
                                    {candidate.skills.length > 3 && (
                                      <span className="text-xs text-gray-500">+{candidate.skills.length - 3} autres</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {selectedCandidate?.id === candidate.id && (
                                <CheckCircle className="text-blue-600" size={20} />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {selectedCandidate && (
                  <section className="rounded-xl border-2 border-blue-500 bg-blue-50 p-5">
                    <h4 className="font-semibold text-blue-900">Candidat sélectionné</h4>
                    <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                      <div><dt className="text-gray-500">Nom</dt><dd className="font-medium text-gray-900">{selectedCandidate.name}</dd></div>
                      <div><dt className="text-gray-500">E-mail</dt><dd className="font-medium text-gray-900">{selectedCandidate.email || 'Non renseigné'}</dd></div>
                      <div><dt className="text-gray-500">Téléphone</dt><dd className="font-medium text-gray-900">{selectedCandidate.phone || 'Non renseigné'}</dd></div>
                      <div><dt className="text-gray-500">Compétences</dt><dd className="font-medium text-gray-900">{selectedCandidate.skills?.join(', ') || 'Non renseignées'}</dd></div>
                    </dl>
                  </section>
                )}
                {errors.existing && <Alert variant="error">{errors.existing}</Alert>}
        </div>
      )}
      {turnoverEnabled && <TurnoverCandidateTab
              active={activeTab === 'turnover'}
              needId={needId}
              onBusyChange={setTurnoverBusy}
              onProcessing={response => {
                turnoverCompletionHandled.current = false;
                setTurnoverAnalysisError(null);
                turnoverCandidateIdRef.current = response.candidateId;
                setAnalysisJobId(response.candidateId);
                setAnalyzingCV(true);
              }}
              analysisError={turnoverAnalysisError}
              onReady={async response => {
                await refreshCandidatesForNeed(needId);
                onSuccess({ id: response.candidateId });
                resetForm();
                onClose();
              }}
              onBatchImported={async (successes, failures) => {
                let refreshFailed = false;
                if (successes.length) {
                  try {
                    await refreshCandidatesForNeed(needId);
                  } catch (_error) {
                    refreshFailed = true;
                  }
                }
                if (failures.length || refreshFailed) return { refreshFailed };
                const candidateIds = successes.map(item => item.response.candidateId);
                onSuccess({ candidateIds }, { batch: true, count: candidateIds.length });
                resetForm();
                onClose();
                return { refreshFailed: false };
              }}
      />}
    </RecruitingCreateModalShell>
  );
};

export default AddCandidateModal;
